
import React, { useMemo, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Legend, Area, ComposedChart, PieChart, Pie, Cell } from 'recharts';
import { Tabs, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { 
  Users, TrendingUp, Bus, BookOpen, Clock, Bell, Activity, Award, ArrowUpRight, 
  UserCheck, GraduationCap, DollarSign, Settings, Loader2, ArrowDownRight,
  Calendar, TrendingDown, CheckCircle2, AlertCircle, Info, ChevronRight,
  BarChart3, Target
} from 'lucide-react';
import axios from 'axios';
import axiosInstance from '@/utils/axiosInstance';
import { API_ENDPOINTS } from '@/constants/config';
import { trackDashboardVisit } from '../SEO/GoogleAnalytics';
import { ROUTES, THEME, TEXT, ACTIVITY_ICON_KEY_ALIASES } from '@/constants/dashboard';

const DashboardContent = () => {
  const navigate = useNavigate();
  const [dashboardStats, setDashboardStats] = useState({
    totalStudents: 0,
    activeRoutes: 0,
    attendanceRate: null,
    avgPerformance: null,
    pendingTasks: null,
    activeTeachers: 0,
  });

  const [enrollmentData, setEnrollmentData] = useState([]);
  const [busRouteData, setBusRouteData] = useState([]);
  const [activeTab, setActiveTab] = useState("overview");
  const [monthlyData, setMonthlyData] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [feeData, setFeeData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashboardComponents, setDashboardComponents] = useState({
    quickActions: {
      enabled: true,
      items: {
        students: true,
        teachers: true,
        routes: true,
        classes: true,
        alerts: true,
        settings: true
      }
    },
    statsCards: {
      enabled: true,
      items: {
        totalStudents: true,
        activeRoutes: true,
        attendance: true,
        performance: true,
        teachers: true,
        pendingTasks: true
      }
    },
    charts: {
      enabled: true,
      items: {
        monthlyTrends: true,
        recentActivity: true,
        feeManagement: true,
        enrollmentTrends: true,
        vehicleRoutes: true
      }
    }
  });

  const activityIconMap = useMemo(() => ({
    bus: Bus,
    bell: Bell,
    users: Users,
    clock: Clock,
    award: Award,
    activity: Activity,
    settings: Settings,
  }), []);

  const resolveActivityIcon = (iconKey) => {
    const normalized = ACTIVITY_ICON_KEY_ALIASES[iconKey] || ACTIVITY_ICON_KEY_ALIASES[String(iconKey || '').trim()] || null;
    return activityIconMap[normalized] || Bus;
  };

  const safeNumber = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

  const domainFromData = (data, keys, { min = 0, pad = 0 } = {}) => {
    if (!Array.isArray(data) || data.length === 0) return [min, "auto"];
    const max = data.reduce((acc, row) => {
      const rowMax = keys.reduce((kAcc, key) => {
        const val = Number(row?.[key]);
        return Number.isFinite(val) ? Math.max(kAcc, val) : kAcc;
      }, 0);
      return Math.max(acc, rowMax);
    }, 0);
    return [min, Math.max(0, max + pad) || "auto"];
  };

  useEffect(() => {
    const checkDashboardAccess = async () => {
      try {
        trackDashboardVisit('schooladmin', 'main');
        const token = localStorage.getItem('token');
        const config = { headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } };
        const response = await axios.get(API_ENDPOINTS.schoolAbout, config);
        const settings = response.data.data.settings;
        if (!settings?.enableDashboard?.enabled) {
          window.location.href = ROUTES.settings;
          return;
        }
        if (settings?.dashboardComponents) {
          setDashboardComponents(settings.dashboardComponents);
        }
      } catch (error) {
        console.error('Error checking dashboard access:', error);
      }
    };
    checkDashboardAccess();
  }, []);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        const response = await axiosInstance.get(API_ENDPOINTS.dashboardStats);
        if (response.data?.status !== 'success') throw new Error('Failed to fetch dashboard');
        const { stats, busRouteData: routeData, enrollmentData: enrollmentTrends, monthlyData: monthlyAttendance, recentActivities: activities, feeData: monthlyFeeData } = response.data.data;
        setDashboardStats({
          totalStudents: Number(stats?.totalStudents) || 0,
          activeRoutes: Number(stats?.activeRoutes) || 0,
          attendanceRate: safeNumber(stats?.attendanceRate),
          avgPerformance: safeNumber(stats?.avgPerformance),
          pendingTasks: safeNumber(stats?.pendingTasks),
          activeTeachers: Number(stats?.activeTeachers) || 0,
        });
        setBusRouteData(routeData || []);
        setEnrollmentData(enrollmentTrends || []);
        setMonthlyData(monthlyAttendance || []);
        setRecentActivities((activities || []).map((a) => ({
          ...a,
          icon: resolveActivityIcon(a?.icon),
        })));
        setFeeData(monthlyFeeData || []);
      } catch (error) {
        console.error('Dashboard fetch error:', error.message, error.response?.data);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, []);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white/95 backdrop-blur-md p-4 border border-gray-200 rounded-xl shadow-xl">
          <p className="font-bold text-gray-900 mb-2 text-sm">{label}</p>
          <div className="space-y-1">
            {payload.map((entry, index) => (
              <div key={index} className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: entry.color }}></span>
                <span className="text-xs text-gray-600">{entry.name}:</span>
                <span className="text-xs font-bold" style={{ color: entry.color }}>
                  {entry.value.toLocaleString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      );
    }
    return null;
  };

  // Get current date
  const getCurrentDate = () => {
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    return new Date().toLocaleDateString('en-US', options);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="text-center">
          <Loader2 className="h-16 w-16 animate-spin mx-auto mb-4" style={{ color: THEME.primary }} />
          <p className="text-gray-600 font-medium">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const STAT_CARDS = [
    {
      id: 'totalStudents',
      title: 'Total Students',
      value: dashboardStats.totalStudents.toLocaleString(),
      change: '+12%',
      trend: 'up',
      icon: Users,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      route: ROUTES.students,
      description: 'Active enrollment'
    },
    {
      id: 'attendance',
      title: 'Attendance Rate',
      value: dashboardStats.attendanceRate === null ? "—" : `${dashboardStats.attendanceRate}%`,
      change: '+2.5%',
      trend: 'up',
      icon: UserCheck,
      color: 'from-green-500 to-emerald-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      route: ROUTES.teacherAttendance,
      description: 'Daily average'
    },
    {
      id: 'performance',
      title: 'Performance',
      value: dashboardStats.avgPerformance === null ? "—" : `${dashboardStats.avgPerformance}%`,
      change: '+5%',
      trend: 'up',
      icon: Award,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      description: 'Average score'
    },
    {
      id: 'teachers',
      title: 'Active Teachers',
      value: dashboardStats.activeTeachers.toLocaleString(),
      change: '+3',
      trend: 'up',
      icon: GraduationCap,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      route: ROUTES.teachers,
      description: 'Teaching staff'
    },
    {
      id: 'activeRoutes',
      title: 'Active Routes',
      value: dashboardStats.activeRoutes.toLocaleString(),
      change: 'Stable',
      trend: 'neutral',
      icon: Bus,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      route: ROUTES.routes,
      description: 'Operating daily'
    },
    {
      id: 'pendingTasks',
      title: 'Pending Tasks',
      value: dashboardStats.pendingTasks === null ? "—" : dashboardStats.pendingTasks.toLocaleString(),
      change: '-5',
      trend: 'down',
      icon: Clock,
      color: 'from-amber-500 to-amber-600',
      bgColor: 'bg-amber-50',
      textColor: 'text-amber-600',
      description: 'Requires attention'
    },
  ];

  const QUICK_ACTIONS = [
    { id: 'students', icon: Users, label: 'Students', route: ROUTES.students, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { id: 'teachers', icon: GraduationCap, label: 'Teachers', route: ROUTES.teachers, color: 'text-indigo-600', bgColor: 'bg-indigo-50' },
    { id: 'routes', icon: Bus, label: 'Routes', route: ROUTES.routes, color: 'text-orange-600', bgColor: 'bg-orange-50' },
    { id: 'classes', icon: BookOpen, label: 'Classes', route: ROUTES.classes, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { id: 'alerts', icon: Bell, label: 'Alerts', route: ROUTES.alerts, color: 'text-red-600', bgColor: 'bg-red-50' },
    { id: 'settings', icon: Settings, label: 'Settings', route: ROUTES.settings, color: 'text-gray-600', bgColor: 'bg-gray-50' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50/30 to-purple-50/30">
      <div className="max-w-[1800px] mx-auto space-y-6">

        {/* Stats Cards */}
        {dashboardComponents?.statsCards?.enabled && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {STAT_CARDS.filter(card => dashboardComponents.statsCards.items?.[card.id]).map((stat) => {
              const Icon = stat.icon;
              const TrendIcon = stat.trend === 'up' ? ArrowUpRight : stat.trend === 'down' ? ArrowDownRight : TrendingUp;
              const trendColor = stat.trend === 'up' ? 'text-green-600 bg-green-50' : stat.trend === 'down' ? 'text-red-600 bg-red-50' : 'text-gray-600 bg-gray-50';
              
              return (
                <div
                  key={stat.id}
                  onClick={() => stat.route && navigate(stat.route)}
                  className="group relative bg-white/80 backdrop-blur-xl rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 border border-white/60 hover:border-blue-200 cursor-pointer overflow-hidden hover:-translate-y-1"
                >
                  {/* Gradient Background */}
                  <div className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-br ${stat.color} opacity-5 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500`}></div>
                  
                  <div className="relative z-10">
                    <div className="flex items-start justify-between mb-4">
                      <div className={`${stat.bgColor} ${stat.textColor} p-3 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                        <Icon className="h-6 w-6" />
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${trendColor} flex items-center gap-1`}>
                        <TrendIcon className="h-3 w-3" />
                        {stat.change}
                      </span>
                    </div>
                    
                    <h3 className="text-sm font-medium text-gray-600 mb-1">{stat.title}</h3>
                    <p className="text-3xl font-bold text-gray-900 mb-1">{stat.value}</p>
                    <p className="text-xs text-gray-500">{stat.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Tabs defaultValue="overview" className="w-full" onValueChange={setActiveTab}>
          <TabsContent value="overview" className="space-y-6">
            {/* Charts Section */}
            {dashboardComponents?.charts?.enabled && (
              <>
                {/* Monthly Trends & Recent Activity */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {dashboardComponents.charts.items?.monthlyTrends && (
                    <Card className="lg:col-span-2 bg-white/80 backdrop-blur-xl border-white/60 shadow-lg hover:shadow-xl transition-all duration-300">
                      <CardHeader className="border-b border-gray-100 pb-4">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                              <Activity className="h-5 w-5 text-blue-600" />
                              {TEXT.charts.monthlyTrends.title}
                            </CardTitle>
                            <CardDescription className="text-sm text-gray-500 mt-1">
                              {TEXT.charts.monthlyTrends.description}
                            </CardDescription>
                          </div>
                          <BarChart3 className="h-8 w-8 text-blue-200" />
                        </div>
                      </CardHeader>
                      <CardContent className="p-6">
                        {monthlyData.length > 0 ? (
                          <div className="h-80">
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={monthlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <defs>
                                  <linearGradient id="attendanceGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                <XAxis 
                                  dataKey="month" 
                                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                  axisLine={{ stroke: '#e5e7eb' }}
                                />
                                <YAxis 
                                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                  axisLine={{ stroke: '#e5e7eb' }}
                                  domain={domainFromData(monthlyData, ["attendance", "performance"], { min: 0 })}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '15px', fontSize: '13px', fontWeight: 500 }} />
                                <Area 
                                  type="monotone" 
                                  dataKey="attendance" 
                                  fill="url(#attendanceGradient)" 
                                  stroke="#3b82f6" 
                                  strokeWidth={3}
                                  name="Attendance %"
                                  dot={{ fill: '#3b82f6', r: 4 }}
                                />
                                <Line 
                                  type="monotone" 
                                  dataKey="performance" 
                                  stroke="#10b981" 
                                  strokeWidth={3} 
                                  dot={{ fill: '#10b981', r: 4 }}
                                  name="Performance %"
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        ) : (
                          <div className="h-80 flex items-center justify-center">
                            <div className="text-center">
                              <Info className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                              <p className="text-gray-500">{TEXT.emptyStates.noMonthlyData}</p>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}

                  {dashboardComponents.charts.items?.recentActivity && (
                    <Card className="bg-white/80 backdrop-blur-xl border-white/60 shadow-lg hover:shadow-xl transition-all duration-300">
                      <CardHeader className="border-b border-gray-100 pb-4">
                        <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                          <Bell className="h-5 w-5 text-indigo-600" />
                          {TEXT.charts.recentActivity.title}
                        </CardTitle>
                        <CardDescription className="text-sm text-gray-500 mt-1">
                          {TEXT.charts.recentActivity.description}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-4">
                        <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar">
                          {recentActivities.length > 0 ? (
                            recentActivities.map((activity, idx) => {
                              const IconComponent = activity.icon;
                              return (
                                <div 
                                  key={idx} 
                                  className="group flex items-start gap-3 p-3 rounded-xl hover:bg-gradient-to-r hover:from-blue-50 hover:to-purple-50 transition-all duration-300 border border-transparent hover:border-blue-100"
                                >
                                  <div className={`${activity.bgColor} p-2.5 rounded-xl group-hover:scale-110 transition-transform duration-300`}>
                                    <IconComponent className={`h-4 w-4 ${activity.iconColor}`} />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-semibold text-gray-900 mb-0.5">{activity.text}</p>
                                    <p className="text-xs text-gray-500 flex items-center gap-1">
                                      <Clock className="h-3 w-3" />
                                      {activity.time}
                                    </p>
                                  </div>
                                  <ChevronRight className="h-4 w-4 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                </div>
                              );
                            })
                          ) : (
                            <div className="flex items-center justify-center py-12">
                              <div className="text-center">
                                <Bell className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                                <p className="text-sm text-gray-500">{TEXT.emptyStates.noRecentActivity}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {/* Fee Management */}
                {dashboardComponents.charts.items?.feeManagement && (
                  <Card className="bg-white/80 backdrop-blur-xl border-white/60 shadow-lg hover:shadow-xl transition-all duration-300">
                    <CardHeader className="border-b border-gray-100 pb-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <DollarSign className="h-5 w-5 text-orange-600" />
                            {TEXT.charts.feeManagement.title}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-500 mt-1">
                            {TEXT.charts.feeManagement.description}
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="p-6">
                      {feeData.length > 0 ? (
                        <div className="h-96">
                          <ResponsiveContainer width="100%" height="100%">
                            <ComposedChart data={feeData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                              <defs>
                                <linearGradient id="createdGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.3}/>
                                </linearGradient>
                                <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#f59e0b" stopOpacity={0.3}/>
                                </linearGradient>
                                <linearGradient id="paidGradient" x1="0" y1="0" x2="0" y2="1">
                                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.8}/>
                                  <stop offset="95%" stopColor="#10b981" stopOpacity={0.3}/>
                                </linearGradient>
                              </defs>
                              <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                              <XAxis 
                                dataKey="month" 
                                tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                axisLine={{ stroke: '#e5e7eb' }}
                              />
                              <YAxis 
                                yAxisId="left"
                                tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                axisLine={{ stroke: '#e5e7eb' }}
                              />
                              <YAxis 
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                axisLine={{ stroke: '#e5e7eb' }}
                              />
                              <Tooltip content={<CustomTooltip />} />
                              <Legend iconType="circle" wrapperStyle={{ paddingTop: '15px', fontSize: '13px', fontWeight: 500 }} />
                              <Bar 
                                yAxisId="left"
                                dataKey="created" 
                                fill="url(#createdGradient)" 
                                name="Fees Created"
                                radius={[8, 8, 0, 0]}
                                barSize={40}
                              />
                              <Bar 
                                yAxisId="left"
                                dataKey="pending" 
                                fill="url(#pendingGradient)" 
                                name="Pending Fees"
                                radius={[8, 8, 0, 0]}
                                barSize={40}
                              />
                              <Bar 
                                yAxisId="left"
                                dataKey="paid" 
                                fill="url(#paidGradient)" 
                                name="Paid Fees"
                                radius={[8, 8, 0, 0]}
                                barSize={40}
                              />
                              <Line 
                                yAxisId="right"
                                type="monotone" 
                                dataKey="pendingAmount" 
                                stroke="#ef4444" 
                                strokeWidth={3}
                                dot={{ fill: '#ef4444', r: 5 }}
                                name="Pending Amount (₹)"
                              />
                              <Line 
                                yAxisId="right"
                                type="monotone" 
                                dataKey="paidAmount" 
                                stroke="#a855f7" 
                                strokeWidth={3}
                                dot={{ fill: '#a855f7', r: 5 }}
                                name="Paid Amount (₹)"
                              />
                            </ComposedChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <div className="h-96 flex items-center justify-center">
                          <div className="text-center">
                            <DollarSign className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                            <p className="text-gray-500">{TEXT.emptyStates.noFeeData}</p>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Enrollment & Vehicle Routes */}
                {(dashboardComponents.charts.items?.enrollmentTrends || dashboardComponents.charts.items?.vehicleRoutes) && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {dashboardComponents.charts.items?.enrollmentTrends && (
                      <Card className={`${dashboardComponents.charts.items?.vehicleRoutes ? 'lg:col-span-2' : 'lg:col-span-3'} bg-white/80 backdrop-blur-xl border-white/60 shadow-lg hover:shadow-xl transition-all duration-300`}>
                        <CardHeader className="border-b border-gray-100 pb-4">
                          <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <TrendingUp className="h-5 w-5 text-blue-600" />
                            {TEXT.charts.enrollmentTrends.title}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-500 mt-1">
                            {TEXT.charts.enrollmentTrends.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                          <div className="h-[450px]">
                            <ResponsiveContainer width="100%" height="100%">
                              <ComposedChart data={enrollmentData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                                <defs>
                                  <linearGradient id="enrollmentGradient" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={THEME.primary} stopOpacity={0.3}/>
                                    <stop offset="95%" stopColor={THEME.primary} stopOpacity={0.05}/>
                                  </linearGradient>
                                </defs>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                <XAxis 
                                  dataKey="year"
                                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                  axisLine={{ stroke: '#e5e7eb' }}
                                />
                                <YAxis 
                                  yAxisId="left"
                                  domain={domainFromData(enrollmentData, ["totalStudents", "targetEnrollment", "newAdmissions", "graduated"], { min: 0, pad: 10 })}
                                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                  axisLine={{ stroke: '#e5e7eb' }}
                                />
                                <YAxis 
                                  yAxisId="right"
                                  orientation="right"
                                  domain={domainFromData(enrollmentData, ["retentionRate"], { min: 0 })}
                                  tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                  axisLine={{ stroke: '#e5e7eb' }}
                                />
                                <Tooltip content={<CustomTooltip />} />
                                <Legend iconType="circle" wrapperStyle={{ paddingTop: '15px', fontSize: '13px', fontWeight: 500 }} />
                                <Area
                                  yAxisId="left"
                                  type="monotone"
                                  dataKey="totalStudents"
                                  fill="url(#enrollmentGradient)"
                                  stroke={THEME.primary}
                                  strokeWidth={3}
                                  name="Total Students"
                                />
                                <Line
                                  yAxisId="left"
                                  type="monotone"
                                  dataKey="targetEnrollment"
                                  stroke={THEME.danger}
                                  strokeDasharray="5 5"
                                  name="Target Enrollment"
                                  strokeWidth={2}
                                  dot={{ fill: THEME.danger, r: 4 }}
                                />
                                <Bar
                                  yAxisId="left"
                                  dataKey="newAdmissions"
                                  fill={THEME.success}
                                  name="New Admissions"
                                  barSize={30}
                                  radius={[8, 8, 0, 0]}
                                />
                                <Bar
                                  yAxisId="left"
                                  dataKey="graduated"
                                  fill={THEME.secondary}
                                  name="Graduated"
                                  barSize={30}
                                  radius={[8, 8, 0, 0]}
                                />
                                <Line
                                  yAxisId="right"
                                  type="monotone"
                                  dataKey="retentionRate"
                                  stroke="#8B5CF6"
                                  name="Retention Rate %"
                                  strokeWidth={3}
                                  dot={{ fill: "#8B5CF6", r: 4 }}
                                />
                              </ComposedChart>
                            </ResponsiveContainer>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {dashboardComponents.charts.items?.vehicleRoutes && (
                      <Card className={`${dashboardComponents.charts.items?.enrollmentTrends ? '' : 'lg:col-span-3'} bg-white/80 backdrop-blur-xl border-white/60 shadow-lg hover:shadow-xl transition-all duration-300`}>
                        <CardHeader className="border-b border-gray-100 pb-4">
                          <CardTitle className="text-xl font-bold text-gray-900 flex items-center gap-2">
                            <Bus className="h-5 w-5 text-orange-600" />
                            {TEXT.charts.vehicleRoutes.title}
                          </CardTitle>
                          <CardDescription className="text-sm text-gray-500 mt-1">
                            {TEXT.charts.vehicleRoutes.description}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="p-6">
                          {busRouteData.length > 0 && busRouteData.some(route => route.students > 0) ? (
                            <div className="h-[450px]">
                              <ResponsiveContainer width="100%" height="100%">
                                <BarChart 
                                  data={busRouteData.filter(route => route.students > 0)} 
                                  margin={{ top: 20, right: 20, left: 20, bottom: 80 }}
                                  layout="vertical"
                                >
                                  <defs>
                                    <linearGradient id="routeGradient" x1="0" y1="0" x2="1" y2="0">
                                      <stop offset="0%" stopColor={THEME.primary} stopOpacity={0.8}/>
                                      <stop offset="100%" stopColor={THEME.primaryDark} stopOpacity={1}/>
                                    </linearGradient>
                                  </defs>
                                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" opacity={0.5} />
                                  <XAxis 
                                    type="number"
                                    tick={{ fill: '#6b7280', fontSize: 12, fontWeight: 500 }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                    domain={[0, 'auto']}
                                    allowDecimals={false}
                                  />
                                  <YAxis 
                                    type="category"
                                    dataKey="route"
                                    width={80}
                                    tick={{ fill: '#6b7280', fontSize: 11, fontWeight: 500 }}
                                    axisLine={{ stroke: '#e5e7eb' }}
                                  />
                                  <Tooltip content={<CustomTooltip />} />
                                  <Bar 
                                    dataKey="students" 
                                    fill="url(#routeGradient)" 
                                    radius={[0, 8, 8, 0]}
                                    name="Students"
                                  />
                                </BarChart>
                              </ResponsiveContainer>
                            </div>
                          ) : (
                            <div className="h-[450px] flex items-center justify-center">
                              <div className="text-center">
                                <Bus className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                                <p className="text-lg font-semibold text-gray-600 mb-2">{TEXT.emptyStates.noRouteDataTitle}</p>
                                <p className="text-sm text-gray-500">{TEXT.emptyStates.noRouteDataHint}</p>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    )}
                  </div>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <style jsx>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f1f5f9;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #cbd5e1;
          border-radius: 10px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #94a3b8;
        }
      `}</style>
    </div>
  );
};

export default DashboardContent;
